import * as React from 'react'

const officialGameTextCache = new Map()

const normalizeKeys = keys => [...new Set(
    (Array.isArray(keys) ? keys : [])
        .map(key => String(key ?? '').trim())
        .filter(Boolean)
)]

const buildCacheKey = (language, key) => `${language}::${key}`

const readCachedTexts = (language, keys) =>
    Object.fromEntries(keys.map(key => {
        const cacheKey = buildCacheKey(language, key)
        return [key, officialGameTextCache.get(cacheKey) ?? '']
    }))

export default function useOfficialGameTexts(language, keys) {
    const signature = normalizeKeys(keys).join('\u0001')
    const normalizedKeys = React.useMemo(
        () => (signature ? signature.split('\u0001') : []),
        [signature]
    )
    const [translations, setTranslations] = React.useState(() =>
        readCachedTexts(language, normalizedKeys)
    )

    React.useEffect(() => {
        const initialTranslations = readCachedTexts(language, normalizedKeys)
        setTranslations(initialTranslations)

        if (!language || normalizedKeys.length === 0)
            return undefined

        const missingKeys = normalizedKeys.filter(key => {
            const cacheKey = buildCacheKey(language, key)
            return !officialGameTextCache.has(cacheKey)
        })

        if (missingKeys.length === 0)
            return undefined

        const controller = new AbortController()
        let active = true

        const loadTranslations = async () => {
            try {
                const response = await fetch('/game-language-texts', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        targetLanguage: language,
                        keys: missingKeys
                    }),
                    signal: controller.signal
                })

                if (!response.ok)
                    throw new Error(`Official game text request failed (${response.status})`)

                const payload = await response.json()
                const translatedMap = payload?.translations && typeof payload.translations === 'object' ?
                    payload.translations :
                    {}

                Object.entries(translatedMap).forEach(([sourceKey, translatedText]) => {
                    officialGameTextCache.set(
                        buildCacheKey(language, sourceKey),
                        String(translatedText ?? '')
                    )
                })

                if (active)
                    setTranslations(readCachedTexts(language, normalizedKeys))
            }
            catch (error) {
                if (error?.name !== 'AbortError')
                    console.warn('[OfficialGameTexts] Could not fetch official game texts:', error)
            }
        }

        loadTranslations()

        return () => {
            active = false
            controller.abort()
        }
    }, [language, normalizedKeys, signature])

    return (key, fallbackText = '') =>
        translations[String(key ?? '').trim()] ||
        String(fallbackText ?? '')
}
